/**
 * NeighborhoodMap Component
 *
 * Simplified neighborhood section that keeps the editable blurb and embedded
 * map, while intentionally avoiding sticky behavior or nearby-attractions UI.
 */

import { useContentToken, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import type { ComponentContentSchema, ComponentDesignSchema } from '@vibesites/runtime';
import { defineComponentRequirements, content, contact } from '@vibesites/tokens';

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {
    'neighborhoodmap.title': {
      label: 'Section Title',
      type: 'text',
      description: 'Main heading for the neighborhood section',
      defaultValue: 'GET TO KNOW THE NEIGHBORHOOD',
    },
    'neighborhoodmap.description': {
      label: 'Description',
      type: 'richtext',
      description: 'Neighborhood description text',
      defaultField: 'property_neighborhood_blurb',
      suggestedFields: ['property_neighborhood_blurb', 'property_short_description'],
      defaultValue: '<p>Discover all the amazing things your neighborhood has to offer.</p>',
    },
    'neighborhoodmap.address': {
      label: 'Map Address',
      type: 'text',
      description: 'Full address used for the embedded map',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    'neighborhoodmap.latitude': {
      label: 'Latitude',
      type: 'text',
      description: 'Latitude coordinate for the map',
      defaultField: 'property_address_latitude',
      defaultValue: '47.7511',
    },
    'neighborhoodmap.longitude': {
      label: 'Longitude',
      type: 'text',
      description: 'Longitude coordinate for the map',
      defaultField: 'property_address_longitude',
      defaultValue: '-122.3458',
    },
  },
};

export const designSchema: ComponentDesignSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {
    'color.background': {
      label: 'Background Color',
      type: 'color',
      description: 'Background color for the section',
      cssVariable: '--vibe-color-background',
    },
    'color.text': {
      label: 'Text Color',
      type: 'color',
      description: 'Primary text color for heading and body copy',
      cssVariable: '--vibe-color-text',
    },
    'color.surface': {
      label: 'Map Surface Color',
      type: 'color',
      description: 'Surface color behind the embedded map',
      cssVariable: '--vibe-color-surface',
    },
    'color.textMuted': {
      label: 'Muted Text Color',
      type: 'color',
      description: 'Secondary text color for supporting copy',
      cssVariable: '--vibe-color-text-muted',
    },
    'font.heading': {
      label: 'Heading Font',
      type: 'font',
      description: 'Font used for the section heading',
      cssVariable: '--vibe-font-heading',
    },
    'font.body': {
      label: 'Body Font',
      type: 'font',
      description: 'Font used for the description text',
      cssVariable: '--vibe-font-body',
    },
    'radius.lg': {
      label: 'Map Corner Radius',
      type: 'radius',
      description: 'Large radius used on the map frame',
      cssVariable: '--vibe-radius-lg',
    },
    'radius.md': {
      label: 'Map Inner Radius',
      type: 'radius',
      description: 'Medium radius available for inner map treatments',
      cssVariable: '--vibe-radius-md',
    },
    'shadow.md': {
      label: 'Map Shadow',
      type: 'shadow',
      description: 'Shadow used for the map card',
      cssVariable: '--vibe-shadow-md',
    },
  },
};

export const requirements = defineComponentRequirements('NeighborhoodMap', {
  designTokens: [
    'color.background',
    'color.text',
    'color.surface',
    'color.textMuted',
    'font.heading',
    'font.body',
    'radius.lg',
    'radius.md',
    'shadow.md',
  ],
  dataTokens: [
    content.optional('property_neighborhood_blurb', 'Neighborhood description'),
    contact.optional('property_full_address', 'Full address for map'),
    contact.optional('property_address_latitude', 'Latitude'),
    contact.optional('property_address_longitude', 'Longitude'),
  ],
});

export function NeighborhoodMap() {
  const sectionTitle = useContentToken('neighborhoodmap.title');
  const address = useContentToken('neighborhoodmap.address');
  const lat = useContentToken('neighborhoodmap.latitude');
  const lng = useContentToken('neighborhoodmap.longitude');

  const mapQuery = (address && address.trim()) || (lat && lng ? `${lat},${lng}` : '');
  const mapUrl = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(mapQuery)}&zoom=14`;

  return (
    <section
      className="bg-background py-16 md:py-24"
      id="neighborhood"
      style={{ scrollMarginTop: 'calc(var(--header-height, 64px) + var(--offer-banner-height, 0px))' }}
    >
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        {/* Keep hidden map tokens editable so address/coordinates remain configurable. */}
        <div className="sr-only" aria-hidden="true">
          <EditableToken path="neighborhoodmap.address" type="text" label="Map Address">
            {address}
          </EditableToken>
          <EditableToken path="neighborhoodmap.latitude" type="text" label="Latitude">
            {lat}
          </EditableToken>
          <EditableToken path="neighborhoodmap.longitude" type="text" label="Longitude">
            {lng}
          </EditableToken>
        </div>

        <div className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="flex flex-col gap-6">
            <EditableToken
              path="neighborhoodmap.title"
              as="h2"
              className="m-0 text-[clamp(1.75rem,3vw,2.5rem)] font-bold uppercase leading-tight text-foreground"
            >
              {sectionTitle}
            </EditableToken>
            <RichTextContent
              path="neighborhoodmap.description"
              as="div"
              className="m-0 text-base leading-relaxed"
              style={{ color: 'var(--vibe-color-text-muted)' }}
            />
          </div>

          <div
            className="overflow-hidden min-h-[20rem] lg:min-h-0 lg:h-full"
            style={{
              backgroundColor: 'var(--vibe-color-surface)',
              borderRadius: 'var(--vibe-radius-lg)',
              boxShadow: 'var(--vibe-shadow-md)',
            }}
          >
            <iframe
              className="h-full min-h-[20rem] w-full border-none lg:min-h-0"
              src={mapUrl}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Property location map"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

(NeighborhoodMap as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;
(NeighborhoodMap as unknown as { designSchema: ComponentDesignSchema }).designSchema = designSchema;
(NeighborhoodMap as unknown as { requirements: typeof requirements }).requirements = requirements;

export default NeighborhoodMap;
